/**
 * `/oms/templates` — global read-only view of every customer's
 * templates (OCP-138). Operators never edit customer templates;
 * this page exists for support triage ("customer says their welcome
 * email is broken — can I see what they have configured").
 *
 * Click a row → side panel with full body_html / body_text /
 * variables_schema. No edit, no delete.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';

import { api, ApiError } from '../../lib/api';
import { UnconfiguredEmptyState, formatDate } from './CustomerDetailPage';

interface Template {
  id: string;
  customer_id?: string | null;
  customer_email?: string | null;
  name: string;
  subject?: string | null;
  body_html?: string | null;
  body_text?: string | null;
  variables_schema?: Record<string, unknown> | null;
  updated_at?: string | null;
  /**
   * OCP-544 - active-content vectors the API found in the stored
   * body_html. Empty for a clean template. Bodies written before the
   * store-time gate can still carry a payload; this is how they get
   * found.
   */
  active_content?: string[];
}

interface TemplatesResponse {
  templates: Template[];
  cursor?: string | null;
  /** How many rows in the unfiltered page carry active content (OCP-544). */
  active_content_total?: number;
}

/** OCP-544 - operator-readable names for the detector's vectors. */
const ACTIVE_CONTENT_LABELS: Record<string, string> = {
  script_tag: '<script>',
  event_handler: 'event handler',
  javascript_url: 'javascript: URL',
  embedded_object: 'iframe/object/base',
  srcdoc_attribute: 'srcdoc',
  meta_refresh: 'meta refresh',
  css_expression: 'css expression()',
};

function ActiveContentBadge({ vectors }: { vectors: string[] }) {
  if (vectors.length === 0) return null;
  return (
    <span
      data-testid="active-content-badge"
      title={vectors.map((v) => ACTIVE_CONTENT_LABELS[v] ?? v).join(', ')}
      className="ml-2 rounded bg-red-100 dark:bg-red-900/40 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-red-800 dark:text-red-200"
    >
      active content
    </span>
  );
}

export function OmsTemplatesPage() {
  const [customer, setCustomer] = useState('');
  const [search, setSearch] = useState('');
  const [data, setData] = useState<TemplatesResponse | null>(null);
  const [state, setState] = useState<'loading' | 'idle' | 'error' | 'unconfigured'>('loading');
  const [errorDetail, setErrorDetail] = useState<string | null>(null);
  const [selected, setSelected] = useState<Template | null>(null);
  // OCP-544 - sweep filter for templates whose stored body still carries
  // active content (payloads planted before the store-time gate existed).
  const [activeContentOnly, setActiveContentOnly] = useState(false);

  const qs = useMemo(() => {
    const p = new URLSearchParams();
    if (customer) p.set('customer', customer);
    if (search) p.set('search', search);
    if (activeContentOnly) p.set('active_content_only', 'true');
    p.set('limit', '100');
    return p.toString();
  }, [customer, search, activeContentOnly]);

  const load = useCallback(async () => {
    setState('loading');
    try {
      const r = await api<TemplatesResponse>(`/v1/admin/oms/templates?${qs}`);
      setData(r);
      setState('idle');
    } catch (err) {
      if (err instanceof ApiError && err.status === 503) {
        setState('unconfigured');
        return;
      }
      if (err instanceof ApiError) {
        const body = err.body as { error?: string; detail?: string } | null;
        setErrorDetail(body?.detail ?? body?.error ?? `HTTP ${err.status}`);
      } else {
        setErrorDetail(err instanceof Error ? err.message : 'Unknown');
      }
      setState('error');
    }
  }, [qs]);

  useEffect(() => {
    void load();
  }, [load]);

  if (state === 'unconfigured') return <UnconfiguredEmptyState />;

  return (
    <div>
      <header className="mb-4">
        <h1 className="text-2xl font-semibold tracking-tight">orboto Mail · Templates</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Read-only view of every customer's email templates. Operators don't edit
          templates — customers do.
        </p>
      </header>

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-xs">
          <span className="text-gray-600 dark:text-gray-400">Customer (email/id)</span>
          <input
            type="search"
            value={customer}
            onChange={(e) => setCustomer(e.target.value)}
            className="w-56 rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs">
          <span className="text-gray-600 dark:text-gray-400">Template name</span>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-56 rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm"
          />
        </label>
        <label className="flex items-center gap-2 pb-1.5 text-xs text-gray-600 dark:text-gray-400">
          <input
            type="checkbox"
            checked={activeContentOnly}
            onChange={(e) => setActiveContentOnly(e.target.checked)}
            className="h-3.5 w-3.5"
          />
          Only templates with active content
        </label>
      </div>

      {(data?.active_content_total ?? 0) > 0 && (
        <div
          role="alert"
          className="mb-4 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-900 dark:text-red-200"
        >
          {data?.active_content_total} template
          {(data?.active_content_total ?? 0) === 1 ? '' : 's'} on this page still
          store active content (scripts, event handlers, javascript: URLs). New
          writes are refused since OCP-544; these predate the gate. The customer
          has to re-save them - the portal preview and the CSP already stop them
          from executing.
        </div>
      )}

      {state === 'error' && (
        <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 p-4">
          <h2 className="text-sm font-semibold text-red-900 dark:text-red-200">Could not load templates</h2>
          {errorDetail && (
            <p className="mt-1 break-all text-xs text-red-800 dark:text-red-300">{errorDetail}</p>
          )}
        </div>
      )}

      <div className="flex gap-4">
        <div className={selected ? 'w-1/2' : 'w-full'}>
          {data && data.templates.length > 0 ? (
            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-800 text-left text-xs uppercase text-gray-500">
                  <tr>
                    <th className="px-3 py-2">Customer</th>
                    <th className="px-3 py-2">Template</th>
                    <th className="px-3 py-2">Subject</th>
                    <th className="px-3 py-2">Variables</th>
                    <th className="px-3 py-2 text-right">Updated</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {data.templates.map((t) => (
                    <tr
                      key={t.id}
                      onClick={() => setSelected(t)}
                      className={`cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/60 ${
                        selected?.id === t.id ? 'bg-brand-50/50' : ''
                      }`}
                    >
                      <td className="px-3 py-2 text-gray-600 dark:text-gray-400">
                        {t.customer_email ?? t.customer_id?.slice(0, 8) ?? '—'}
                      </td>
                      <td className="px-3 py-2 font-medium">
                        {t.name}
                        <ActiveContentBadge vectors={t.active_content ?? []} />
                      </td>
                      <td className="truncate px-3 py-2 text-gray-700 dark:text-gray-300">
                        {t.subject ?? '—'}
                      </td>
                      <td className="px-3 py-2 text-gray-500">
                        {t.variables_schema
                          ? `${Object.keys(t.variables_schema).length} vars`
                          : '—'}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-500">
                        {t.updated_at ? formatDate(t.updated_at) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            state === 'idle' && (
              <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-6 text-sm text-gray-500">
                No templates match the current filters.
              </div>
            )
          )}
        </div>

        {selected && <TemplateDetail template={selected} onClose={() => setSelected(null)} />}
      </div>
    </div>
  );
}

function TemplateDetail({
  template,
  onClose,
}: {
  template: Template;
  onClose: () => void;
}) {
  return (
    <aside className="w-1/2 overflow-hidden rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
      <header className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
        <div>
          <h2 className="text-sm font-semibold">{template.name}</h2>
          {template.subject && (
            <p className="text-xs text-gray-500">Subject: {template.subject}</p>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="rounded p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800/60"
        >
          ✕
        </button>
      </header>
      <div className="flex flex-col gap-4 px-4 py-3 text-sm">
        {(template.active_content?.length ?? 0) > 0 && (
          <section
            role="alert"
            className="rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 p-2 text-xs text-red-900 dark:text-red-200"
          >
            <strong className="block">Stored active content</strong>
            {(template.active_content ?? [])
              .map((v) => ACTIVE_CONTENT_LABELS[v] ?? v)
              .join(', ')}
            . The body below is shown as escaped text, never rendered - and the
            customer portal previews it in a sandboxed frame (OCP-544).
          </section>
        )}
        {template.variables_schema && (
          <section>
            <h3 className="mb-1 text-xs font-semibold uppercase text-gray-500">
              Variables
            </h3>
            <pre className="max-h-40 overflow-auto rounded bg-gray-50 dark:bg-gray-800 p-2 text-xs">
              {JSON.stringify(template.variables_schema, null, 2)}
            </pre>
          </section>
        )}
        {template.body_html && (
          <section>
            <h3 className="mb-1 text-xs font-semibold uppercase text-gray-500">
              body_html
            </h3>
            <pre className="max-h-72 overflow-auto rounded bg-gray-50 dark:bg-gray-800 p-2 text-xs">
              {template.body_html}
            </pre>
          </section>
        )}
        {template.body_text && (
          <section>
            <h3 className="mb-1 text-xs font-semibold uppercase text-gray-500">
              body_text
            </h3>
            <pre className="max-h-72 overflow-auto rounded bg-gray-50 dark:bg-gray-800 p-2 text-xs">
              {template.body_text}
            </pre>
          </section>
        )}
      </div>
    </aside>
  );
}
